import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../services/firebase';
import { clsx } from 'clsx';
import InlineMathField from '../math/InlineMathField';
import { MathUtils } from '../math/MathUtils';
import MathRenderer from '../math/MathRenderer';

const normalizeLatexPreview = (content = '') =>
  String(content || '').replace(/\\degree\b/g, '^\\circ');

const getModelAnswers = (config) => {
  if (!config) return [];
  if (config.type === 'multiple-choice') {
    return config.correctAnswer ? [config.correctAnswer] : [];
  }
  if (Array.isArray(config.correctAnswers)) {
    return config.correctAnswers.filter(Boolean);
  }
  if (config.correctAnswer) {
    return [config.correctAnswer];
  }
  return [];
};

const hasAnsweredResponse = (config, value) => {
  if (config?.type === 'multi-select') return Array.isArray(value) && value.length > 0;
  if (config?.type === 'image-upload') return Boolean(value);
  return String(value ?? '').trim().length > 0;
};

const ResponseAreaRenderer = ({
  blockId,
  config,
  value,
  onChange,
  disabled,
  userId,
  showModelAnswer = false,
  enableModelAnswerToggle = false,
  canAccessModelAnswers = true,
  modelAnswerLockedMessage = '',
  onModelAnswerLockedClick = null,
  showDeleteButton = false,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [isModelAnswerVisible, setIsModelAnswerVisible] = useState(false);
  const fileInputRef = useRef(null);
  const currentValue = value ?? '';
  const modelAnswers = getModelAnswers(config);
  const isAnswered = hasAnsweredResponse(config, currentValue);

  useEffect(() => {
    if (!enableModelAnswerToggle) return;
    if (!canAccessModelAnswers || !isAnswered) {
      setIsModelAnswerVisible(false);
    }
  }, [canAccessModelAnswers, enableModelAnswerToggle, isAnswered]);

  if (!config) return null;

  const OptionMathText = ({ content, className = '' }) => {
    const containerRef = useRef(null);

    useEffect(() => {
      if (!containerRef.current) return;
      const container = containerRef.current;
      const source = normalizeLatexPreview(content || '').replace(/\s*\n+\s*/g, ' ');
      container.innerHTML = '';

      const regex = /\$\$([\s\S]+?)\$\$|\$([^$]+?)\$/g;
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(source)) !== null) {
        const [raw, display, inline] = match;
        const textBefore = source.slice(lastIndex, match.index);
        if (textBefore) {
          container.appendChild(document.createTextNode(textBefore));
        }

        const latex = (display || inline || '').trim();
        if (latex) {
          const span = document.createElement('span');
          span.className = 'math-container';
          span.contentEditable = 'false';
          span.setAttribute('data-math-content', latex);
          span.innerHTML = `\\(${latex}\\)`;
          container.appendChild(span);
        } else {
          container.appendChild(document.createTextNode(raw));
        }

        lastIndex = match.index + raw.length;
      }

      if (lastIndex < source.length) {
        container.appendChild(document.createTextNode(source.slice(lastIndex)));
      }

      MathUtils.renderMath(container);
    }, [content]);

    return <span ref={containerRef} className={className} />;
  };

  const shouldShowModelAnswerPanel = enableModelAnswerToggle
    ? isModelAnswerVisible && canAccessModelAnswers && isAnswered
    : showModelAnswer;

  const modelAnswerPanel = shouldShowModelAnswerPanel && modelAnswers.length > 0 ? (
    <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-emerald-700 mb-1">Model Answer</div>
      <div className="space-y-2">
        {modelAnswers.map((answer, index) => (
          <MathRenderer
            key={`model-answer-${blockId}-${index}`}
            content={normalizeLatexPreview(answer || '')}
            className="text-sm text-emerald-900"
          />
        ))}
      </div>
    </div>
  ) : null;

  const modelAnswerButton = enableModelAnswerToggle ? (
    <div className="mt-2">
      {modelAnswers.length > 0 ? (
        <button
          type="button"
          onClick={() => setIsModelAnswerVisible((prev) => !prev)}
          disabled={!canAccessModelAnswers || !isAnswered}
          title={
            !canAccessModelAnswers
              ? modelAnswerLockedMessage || 'Sign in to reveal model answers'
              : isAnswered
                ? 'Toggle model answer'
                : 'Provide a response to unlock the model answer'
          }
          className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          {isModelAnswerVisible ? 'Hide Model Answer' : 'Show Model Answer'}
        </button>
      ) : null}
      {!canAccessModelAnswers && (
        typeof onModelAnswerLockedClick === 'function' ? (
          <button
            type="button"
            onClick={onModelAnswerLockedClick}
            className="mt-1 text-xs font-medium text-amber-700 underline decoration-amber-300 underline-offset-2 hover:text-amber-800"
          >
            {modelAnswerLockedMessage || 'Sign in to reveal model answers.'}
          </button>
        ) : (
          <p className="mt-1 text-xs text-amber-700">
            {modelAnswerLockedMessage || 'Sign in to reveal model answers.'}
          </p>
        )
      )}
      {canAccessModelAnswers && !isAnswered && (
        <p className="mt-1 text-xs text-gray-500">
          Answer this response to unlock the model answer.
        </p>
      )}
      {canAccessModelAnswers && isAnswered && modelAnswers.length === 0 && (
        <p className="mt-1 text-xs text-gray-500">
          No model answer is configured for this response.
        </p>
      )}
    </div>
  ) : null;

  // Multiple choice — radio buttons
  if (config.type === 'multiple-choice') {
    return (
      <div className="relative space-y-2">
        {showDeleteButton && (
          <button
            type="button"
            className="response-area-preview-delete absolute right-0 top-0 z-10"
            data-response-action="delete"
            aria-label="Delete response block"
            title="Delete response block"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M3 6h18" />
              <path d="M8 6V4h8v2" />
              <path d="M19 6l-1 14H6L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
            </svg>
          </button>
        )}
        {config.label && <div className="text-sm font-medium text-gray-700 mb-1">{config.label}</div>}
        {(config.options || []).filter(o => o.trim()).map((opt, idx) => (
          <label key={idx} className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`ra-${blockId}`}
              value={opt}
              checked={currentValue === opt}
              onChange={() => onChange(opt)}
              disabled={disabled}
              className="text-sigma-600 focus:ring-sigma-500"
            />
            <OptionMathText content={opt} className="text-sm text-gray-800" />
          </label>
        ))}
        {modelAnswerButton}
        {modelAnswerPanel}
      </div>
    );
  }

  // Multi select — checkboxes
  if (config.type === 'multi-select') {
    const selectedValues = Array.isArray(currentValue) ? currentValue : [];
    const handleToggle = (opt) => {
      if (disabled) return;
      const next = selectedValues.includes(opt)
        ? selectedValues.filter(v => v !== opt)
        : [...selectedValues, opt];
      onChange(next);
    };
    return (
      <div className="relative space-y-2">
        {showDeleteButton && (
          <button
            type="button"
            className="response-area-preview-delete absolute right-0 top-0 z-10"
            data-response-action="delete"
            aria-label="Delete response block"
            title="Delete response block"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M3 6h18" />
              <path d="M8 6V4h8v2" />
              <path d="M19 6l-1 14H6L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
            </svg>
          </button>
        )}
        {config.label && <div className="text-sm font-medium text-gray-700 mb-1">{config.label}</div>}
        {(config.options || []).filter(o => o.trim()).map((opt, idx) => (
          <label key={idx} className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedValues.includes(opt)}
              onChange={() => handleToggle(opt)}
              disabled={disabled}
              className="rounded border-gray-300 text-sigma-600 focus:ring-sigma-500"
            />
            <OptionMathText content={opt} className="text-sm text-gray-800" />
          </label>
        ))}
        {modelAnswerButton}
        {modelAnswerPanel}
      </div>
    );
  }

  const renderTextResponse = ({ rows, textareaClassName }) => (
    <div className="relative">
      {showDeleteButton && (
        <button
          type="button"
          className="response-area-preview-delete absolute right-0 top-0 z-10"
          data-response-action="delete"
          aria-label="Delete response block"
          title="Delete response block"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M3 6h18" />
            <path d="M8 6V4h8v2" />
            <path d="M19 6l-1 14H6L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
          </svg>
        </button>
      )}
      {config.label && <div className="text-sm font-medium text-gray-700 mb-1">{config.label}</div>}
      <InlineMathField
        value={currentValue}
        onChange={(val) => onChange(val)}
        placeholder="Type your answer..."
        rows={rows}
        textareaClassName={textareaClassName}
        buttonLabel="Insert Math (Ctrl+M)"
        buttonClassName="text-xs text-sigma-700 hover:text-sigma-900"
        buttonTitle="Insert mathematical expression (Ctrl+M)"
        showButton={!disabled}
        disabled={disabled}
      />
      {!disabled && (
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={() => setShowPreview((prev) => !prev)}
            className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700"
          >
            {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {showPreview ? 'Hide Preview' : 'Preview Math'}
          </button>
        </div>
      )}
      {showPreview && (
        <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Preview</div>
          <MathRenderer
            content={normalizeLatexPreview(currentValue || '')}
            className="text-sm text-gray-800"
            enableMarkdown={false}
          />
        </div>
      )}
      {modelAnswerButton}
      {modelAnswerPanel}
    </div>
  );

  // Short answer
  if (config.type === 'short-answer') {
    return renderTextResponse({
      rows: 1,
      textareaClassName:
        'text-sm rounded-lg focus:ring-sigma-500 focus:border-sigma-500 disabled:bg-gray-50 resize-none',
    });
  }

  // Paragraph
  if (config.type === 'paragraph') {
    return renderTextResponse({
      rows: 4,
      textareaClassName:
        'text-sm rounded-lg focus:ring-sigma-500 focus:border-sigma-500 resize-y disabled:bg-gray-50',
    });
  }

  // Image upload
  if (config.type === 'image-upload') {
    const handleUpload = async (file) => {
      if (!file || !file.type.startsWith('image/')) {
        setUploadError('Please select a valid image file.');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setUploadError('Image must be under 10 MB.');
        return;
      }
      setUploading(true);
      setUploadError('');
      try {
        const timestamp = Date.now();
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `sigma-submissions/${userId}/${timestamp}_${safeName}`;
        const storageRef = ref(storage, path);
        const snapshot = await uploadBytes(storageRef, file);
        const url = await getDownloadURL(snapshot.ref);
        onChange(url);
      } catch (err) {
        console.error('Upload failed:', err);
        setUploadError('Upload failed. Please try again.');
      } finally {
        setUploading(false);
      }
    };

    return (
      <div className="relative">
        {showDeleteButton && (
          <button
            type="button"
            className="response-area-preview-delete absolute right-0 top-0 z-10"
            data-response-action="delete"
            aria-label="Delete response block"
            title="Delete response block"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M3 6h18" />
              <path d="M8 6V4h8v2" />
              <path d="M19 6l-1 14H6L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
            </svg>
          </button>
        )}
        {config.label && <div className="text-sm font-medium text-gray-700 mb-1">{config.label}</div>}
        {currentValue ? (
          <div className="relative inline-block">
            <img src={currentValue} alt="Uploaded" className="max-h-48 rounded border border-gray-200" />
            {!disabled && (
              <button
                onClick={() => onChange('')}
                className="absolute top-1 right-1 w-6 h-6 bg-white rounded-full shadow flex items-center justify-center text-gray-500 hover:text-red-500"
              >
                <X size={14} />
              </button>
            )}
          </div>
        ) : (
          <div
            className={clsx(
              'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
              disabled ? 'border-gray-200 bg-gray-50' : 'border-gray-300 hover:border-sigma-400 hover:bg-sigma-50'
            )}
            onClick={() => !disabled && fileInputRef.current?.click()}
          >
            <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
            <p className="text-sm text-gray-500">
              {uploading ? 'Uploading...' : 'Click to upload an image'}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUpload(file);
              }}
              disabled={disabled}
            />
          </div>
        )}
        {modelAnswerButton}
        {modelAnswerPanel}
        {uploadError && <p className="mt-1 text-sm text-red-600">{uploadError}</p>}
      </div>
    );
  }

  return <div className="text-sm text-gray-400">Unknown response type: {config.type}</div>;
};

export default ResponseAreaRenderer;
